import { combineReducers } from "redux";
import { createAction, createReducer } from "@reduxjs/toolkit";

const MODE_ADD_ROWS_UPDATED = "MODE_ADD_ROWS_UPDATED";
const MODE_ADD_ROW_ADDED = "MODE_ADD_ROW_ADDED";
const MODE_ADD_ROW_REMOVED = "MODE_ADD_ROW_REMOVED";
const MODE_ADD_ROW_UPDATED = "MODE_ADD_ROW_UPDATED";
export const MODE_ADD_ROW_COORDINATES_SWAPPED =
  "MODE_ADD_ROW_COORDINATES_SWAPPED";
const MODE_ADD_ROW_TYPE_CHANGED = "MODE_ADD_ROW_TYPE_CHANGED";
const MODE_ADD_WAS_RESET = "MODE_ADD_WAS_RESET";

export const declareSavingProcessStarted = createAction<void>(
  "MODE_ADD_SAVING_PROCESS_STARTED",
);
export const declareSavingProcessFinished = createAction<void>(
  "MODE_ADD_SAVING_PROCESS_FINISHED",
);
export const declareSavingProcessFailed = createAction<Error>(
  "MODE_ADD_SAVING_PROCESS_FAILED",
);

interface ISavingProcessState {
  pending: boolean;
  error: string;
}

export type GcpCsvRowType = "GCP" | "FAKE_GCP" | "QC";

export interface IGcpCsvRow {
  type: GcpCsvRowType;
  name: string;
  easting: string;
  northing: string;
  elevation: string;
  nameErrorMessage: string | null;
  eastingErrorMessage: string | null;
  northingErrorMessage: string | null;
  elevationErrorMessage: string | null;
}

export interface IModeAddUi {
  savingProcess: ISavingProcessState;
  gcpCsvRows: IGcpCsvRow[];
}

interface IUpdateRowPayload {
  index: number;
  newRow: IGcpCsvRow;
}

interface IChangeTypePayload {
  index: number;
  type: GcpCsvRowType;
}

export const updateRows = (rows: IGcpCsvRow[]): IAction<IGcpCsvRow[]> => ({
  payload: rows,
  type: MODE_ADD_ROWS_UPDATED,
});

export const addRow = (newRow: IGcpCsvRow): IAction<IGcpCsvRow> => ({
  payload: newRow,
  type: MODE_ADD_ROW_ADDED,
});

export const removeRow = (index: number): IAction<number> => ({
  payload: index,
  type: MODE_ADD_ROW_REMOVED,
});

export const updateRow = (
  index: number,
  newRow: IGcpCsvRow,
): IAction<IUpdateRowPayload> => ({
  payload: {
    index,
    newRow,
  },
  type: MODE_ADD_ROW_UPDATED,
});

export const addEmptyRow = (): IAction<IGcpCsvRow> =>
  addRow({
    name: "",
    type: "GCP",
    easting: "",
    northing: "",
    elevation: "",
    nameErrorMessage: null,
    eastingErrorMessage: null,
    northingErrorMessage: null,
    elevationErrorMessage: null,
  });

export const swapRowCoordinates = (index: number): IAction<number> => ({
  payload: index,
  type: MODE_ADD_ROW_COORDINATES_SWAPPED,
});

export const changeRowType = (
  index: number,
  type: GcpCsvRowType,
): IAction<IChangeTypePayload> => ({
  payload: {
    index,
    type,
  },
  type: MODE_ADD_ROW_TYPE_CHANGED,
});

export const resetGCPCSV = (): IAction<void> => ({
  payload: null,
  type: MODE_ADD_WAS_RESET,
});

export const getCsvTable = (state: IModeAddUi): IGcpCsvRow[] =>
  state.gcpCsvRows;

function modeAddUiReducer(
  state: IGcpCsvRow[] = [],
  action: IAction<any>,
): IGcpCsvRow[] {
  switch (action.type) {
    case MODE_ADD_ROWS_UPDATED:
      return action.payload;
    case MODE_ADD_ROW_ADDED:
      return [...state, action.payload];
    case MODE_ADD_ROW_REMOVED: {
      const a = [].concat(state);
      a.splice(action.payload, 1);
      return a;
    }
    case MODE_ADD_ROW_UPDATED:
      return state.map((item, index) => {
        if (index === action.payload.index) {
          return { ...action.payload.newRow };
        }
        return { ...item };
      });
    case MODE_ADD_ROW_COORDINATES_SWAPPED:
      return state.map((item, index) => {
        if (index === action.payload) {
          return {
            ...item,
            easting: item.northing,
            northing: item.easting,
          };
        }
        return { ...item };
      });
    case MODE_ADD_ROW_TYPE_CHANGED:
      return state.map((item, index) => {
        if (index === action.payload.index) {
          return {
            ...item,
            type: action.payload.type,
          };
        }
        return { ...item };
      });
    case MODE_ADD_WAS_RESET:
      return [];
    default:
      return state;
  }
}

const savingProcessReducer = createReducer(
  {
    pending: false,
    error: null,
  },
  (builder) =>
    builder
      .addCase(declareSavingProcessStarted, (state, action) => ({
        error: null,
        pending: true,
      }))
      .addCase(declareSavingProcessFinished, (state, action) => ({
        error: null,
        pending: false,
      }))
      .addCase(declareSavingProcessFailed, (state, action) => ({
        error: action.payload,
        pending: false,
      })),
);

export default combineReducers({
  gcpCsvRows: modeAddUiReducer,
  savingProcess: savingProcessReducer,
});
